import isEmpty from 'lodash/isEmpty'
import { useSetAtom } from 'jotai'
import { useMemo } from 'react'
import { useQueryMembershipEntryPointBanner } from '@/features/shop/services/Banners/queries'
import { BannerContents } from '@/features/shop/components/HeroPromoBanner/BannerContents'
import styled from 'styled-components'
import {
  EntryPointBannerTracking,
  EntryPointBannerTypes,
} from '@/features/shop/services/Banners/constants'
import { IconButton } from '@shipt/design-system-buttons'
import { LogoMarkIcon, NegativeIcon } from '@shipt/design-system-icons'
import { useMembershipModals } from '@/features/account/components/MembershipManagement/useMembershipModals'
import { membershipEntryPointBannerDismissedAtom } from '@/features/account/state/UserPreferences/atom'
import { SkeletonLoader } from '@/features/shared/components/SkeletonLoader'
import { ScrollIntoView } from '@/features/shared/components/ScrollIntoView'
import { trackElementClicked, trackElementViewed } from '@/features/shared/analytics/element'
import { useSegwayRouter } from '@/features/shared/hooks/useSegwayRouter'
import { routes } from '@shared/constants/routes'
import { useHandleDialog } from '@/features/shared/state/Dialog/useHandleDialog'
import { DynamicMembershipPurchaseModal } from '@/features/account/components/Modal/MembershipPurchaseModal/MembershipPurchaseModal/DynamicMembershipPurchaseModal'
import { screenSizes } from '@shipt/design-system-themes'
import { useQuerySubscriptionPlans } from '@/features/account/services/SubscriptionAux/queries'
import { getEarlyRenewalPlan } from '@/features/account/services/SubscriptionAux/utils'
import { spacing } from '@/features/shared/theme/tokens'
import { type PlaceholderData } from '@/features/cms/components/CMS/types'
import { GridLayoutContainer } from '@/features/shared/styles/layout'
import { getCMSWrapperProps } from '@/features/cms/components/CMS/utils/getCMSWrapperProps'
import { useShouldFetchMembershipEntryPointBanner } from '@/features/account/components/MembershipEntryPointBanner/hooks'
import { Row } from '@shipt/design-system-layouts'
import { useIsGuestUser } from '@/features/account/services/User/hooks'
import { usePersona } from '@/features/business/hooks/usePersona'
import { theme } from '@/features/shared/constants/shiptTheme'

export const MembershipEntryPointBanner = ({
  content_type_id,
  id,
}: Pick<PlaceholderData, 'content_type_id' | 'id'>) => {
  const setIsDismissed = useSetAtom(membershipEntryPointBannerDismissedAtom)

  const router = useSegwayRouter()
  const shouldFetchBanner = useShouldFetchMembershipEntryPointBanner()
  const {
    data: bannerData,
    isPending,
    error,
  } = useQueryMembershipEntryPointBanner(shouldFetchBanner)
  const { openDialog } = useHandleDialog()
  const { data: deliveryPlans } = useQuerySubscriptionPlans()
  const { earlyRenewalPlan } = getEarlyRenewalPlan(deliveryPlans ?? [])
  const isGuestUser = useIsGuestUser()
  const { isBusinessNonOwner } = usePersona()

  const { type, title = '', sub_title = '', original_price } = bannerData || {}

  const eventProperties = useMemo(
    () => ({
      display_sublocation: EntryPointBannerTracking.DISPLAY_SUBLOCATION.GLOBAL_HOME,
      type: EntryPointBannerTracking.type.BANNER,
      content: type,
      message_goal: EntryPointBannerTracking.MESSAGE_GOAL,
    }),
    [type],
  )
  const { openMembershipChangeModal } = useMembershipModals()

  const handleButtonAction = () => {
    if (isGuestUser) {
      router.push(routes.PRICING.url)
      trackElementClicked(eventProperties)
      return
    }
    if (
      type === EntryPointBannerTypes.MEMBER_UPGRADE ||
      type === EntryPointBannerTypes.MEMBER_UPGRADE_WITH_PROMO
    ) {
      openMembershipChangeModal()
    } else if (type === EntryPointBannerTypes.MEMBER_EARLY_RENEWAL && earlyRenewalPlan) {
      openDialog(DynamicMembershipPurchaseModal, {
        plan: earlyRenewalPlan,
        modalTitle: 'Renew annual membership',
      })
    } else {
      router.push(routes.MEMBERSHIP_DASHBOARD.url)
    }
    trackElementClicked(eventProperties)
  }

  const handleCloseBanner = () => {
    setIsDismissed(true)
  }

  const getButtonLabel = () => {
    if (
      type === EntryPointBannerTypes.PPO_WITH_PROMO ||
      type === EntryPointBannerTypes.MEMBER_EARLY_RENEWAL
    ) {
      return 'Redeem offer'
    }
    if (type === EntryPointBannerTypes.MEMBER_UPGRADE_WITH_PROMO) {
      return 'Upgrade plan'
    }
    return sub_title
  }

  const hideBanner =
    !shouldFetchBanner ||
    // business members who are not the group owner cannot purchase membership
    isBusinessNonOwner ||
    // api returns an error when users are ineligible
    Boolean(error) ||
    isEmpty(bannerData)

  const isPromo =
    type === EntryPointBannerTypes.PPO_WITH_PROMO ||
    type === EntryPointBannerTypes.MEMBER_UPGRADE_WITH_PROMO ||
    type === EntryPointBannerTypes.MEMBER_EARLY_RENEWAL

  const expiresString = isPromo ? sub_title : undefined

  const originalPrice = isPromo && original_price ? `$${original_price / 100}` : undefined

  if (hideBanner) return null
  if (isPending) return <SkeletonLoader isLoading height={76} width="100%" />
  return (
    <GridLayoutContainer {...getCMSWrapperProps({ content_type_id, id })}>
      <ScrollIntoView onInView={() => trackElementViewed(eventProperties)}>
        <BannerWrapper align="center" padding={['md', 'lg']} spacing="md">
          <BannerContents
            id={type}
            surface="inverse"
            leadingSlot={<Logo />}
            textColor="white"
            title={title}
            buttonLabel={getButtonLabel()}
            buttonAction={handleButtonAction}
            expiresString={expiresString}
            originalPrice={originalPrice}
            isHome
            termsApply={type === EntryPointBannerTypes.MEMBER_EARLY_RENEWAL}
          />
          <IconButton
            onClick={handleCloseBanner}
            icon={NegativeIcon}
            size="md"
            variant="ghost"
            surface="inverse"
            aria-describedby={`close_${type}_banner`}
            aria-label="Close banner"
          />
        </BannerWrapper>
      </ScrollIntoView>
    </GridLayoutContainer>
  )
}

const BannerWrapper = styled(Row)`
  border-radius: 0.5rem;
  background-color: ${theme.color.background.base.brand.primary};
  margin-top: ${spacing('lg')};
  margin-bottom: ${spacing('lg')};

  @media ${screenSizes.max_smMobile} {
    margin-top: 0;
  }
`
const Logo = styled(LogoMarkIcon)`
  width: 24px;
  height: 24px;
`
